import * as React from "react";
import { cn } from "@/lib/utils";

export interface ScrollFadeAreaProps {
  /** 스크롤 컨테이너 className */
  className?: string;
  /** 바깥 래퍼(relative) className */
  wrapperClassName?: string;
  /**
   * 스크롤 컨테이너 ref. 에러 필드로 스크롤하는 등 외부에서 컨테이너를 직접 만져야 할 때 넘긴다
   * (예: `focusFirstErrorField`).
   */
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  /** 페이드 갱신과 함께 호출되는 추가 스크롤 처리 (예: 무한 스크롤) */
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  /** 하단 페이드 표시 여부. 아래에 섀도가 있는 요소가 붙으면 끈다 */
  showBottomFade?: boolean;
  children: React.ReactNode;
}

type FadeState = { top: boolean; bottom: boolean };

/**
 * 스크롤 끝에 닿았는지 판정.
 * 브라우저가 scrollTop 을 소수점으로 들고 있어 끝에서도 정확히 0 / maxScroll 이 되지 않는다 —
 * 1px 여유를 두지 않으면 맨 위·맨 아래에서 페이드가 깜빡인다.
 */
function getFadeState(el: HTMLElement): FadeState {
  const maxScroll = el.scrollHeight - el.clientHeight;
  return { top: el.scrollTop > 1, bottom: el.scrollTop < maxScroll - 1 };
}

/** 값이 같으면 이전 객체를 그대로 반환한다 — 스크롤 이벤트마다 리렌더되지 않게 한다 */
function nextFade(prev: FadeState, el: HTMLElement): FadeState {
  const next = getFadeState(el);
  return prev.top === next.top && prev.bottom === next.bottom ? prev : next;
}

/**
 * 상·하단 페이드 그라데이션이 붙는 스크롤 영역.
 *
 * 페이드는 "그 방향에 더 볼 내용이 남아 있다"는 신호이므로 스크롤이 끝에 닿으면 해당 방향만
 * 사라진다 — 맨 위에서 상단 페이드, 맨 아래에서 하단 페이드를 함께 보여주면 잘리지 않은 내용을
 * 흐리게 덮어 UX 가 어색해진다. 내용이 넘치지 않으면 양쪽 모두 표시하지 않는다.
 */
export function ScrollFadeArea({
  className,
  wrapperClassName,
  scrollRef,
  onScroll,
  showBottomFade = true,
  children,
}: ScrollFadeAreaProps) {
  // ref 대신 state 로 노드를 들고 있어야 아래 effect 가 노드가 붙는 시점에 다시 돈다
  const [scrollEl, setScrollEl] = React.useState<HTMLDivElement | null>(null);
  const [fade, setFade] = React.useState<FadeState>({
    top: false,
    bottom: false,
  });

  React.useEffect(() => {
    if (!scrollEl) return;

    // 내용 높이가 바뀌면(비동기 로딩, 섹션 펼침/접힘) 스크롤 가능 여부 자체가 달라지므로
    // 컨테이너와 그 자식들을 함께 관찰한다 — 스크롤 이벤트만으로는 잡히지 않는다.
    // observe() 는 등록 직후 초기 크기로 콜백을 한 번 호출하므로 최초 상태도 여기서 정해진다
    // (effect 본문에서 setFade 를 직접 부르면 react-hooks/set-state-in-effect 에 걸린다).
    const observer = new ResizeObserver(() =>
      setFade((prev) => nextFade(prev, scrollEl)),
    );
    observer.observe(scrollEl);
    for (const child of scrollEl.children) observer.observe(child);

    return () => observer.disconnect();
  }, [scrollEl]);

  return (
    <div className={cn("relative", wrapperClassName)}>
      {fade.top && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-10.5 bg-linear-to-b from-bg-primary to-transparent" />
      )}
      {showBottomFade && fade.bottom && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10.5 bg-linear-to-t from-bg-primary to-transparent" />
      )}
      <div
        ref={(node) => {
          setScrollEl(node);
          if (scrollRef) scrollRef.current = node;
        }}
        onScroll={(e) => {
          const el = e.currentTarget;
          setFade((prev) => nextFade(prev, el));
          onScroll?.(e);
        }}
        className={cn("overflow-y-auto", className)}
      >
        {children}
      </div>
    </div>
  );
}
